import { JSX, ReactElement, ReactNode } from "jsx-dom";
import { i18n } from "../../i18n";
import dialogPolyfill from "dialog-polyfill";
import { select } from "@fruugo/utilities";
import { Icon } from "../Icon";
import { disableBodyScroll, enableBodyScroll } from "../../utilities";

type SideMenuPosition = "left" | "right";

export type SideMenuSettings = {
  position: SideMenuPosition;
  disableScroll: boolean;
};

export const sideMenuClassConfig = {
  sideMenuClass: ".side-menu",
  closeSideMenuClass: ".js-close-side-menu",
  sideMenuBodyClass: ".js-side-menu-body",
};

export function openSideMenu(body: JSX.Element, settings: SideMenuSettings) {
  const { disableScroll, position } = settings;
  let sideMenu = select<HTMLDialogElement>(sideMenuClassConfig.sideMenuClass);

  if (disableScroll) disableBodyScroll();

  if (!sideMenu) {
    document.body.appendChild(<SideMenu position={position}>{body}</SideMenu>);
    sideMenu = select<HTMLDialogElement>(sideMenuClassConfig.sideMenuClass);
    sideMenu && void sideMenu.offsetWidth; // Force reflow so the transition happens the first time its added to the page
  } else {
    updateSideMenuPosition(position);
    updateSideMenuBody(body);
  }

  // Polyfill for older browsers like Safari 15.2, can probably remove in 2026 after checking browser support.
  if (sideMenu && !sideMenu.showModal) {
    dialogPolyfill.registerDialog(sideMenu);
  }

  sideMenu?.showModal();
  sideMenu?.addEventListener("mousedown", handleBackDropClick);

  focusFirstSideMenuItem();
}

export function closeSideMenu() {
  const sideMenu = select<HTMLDialogElement>(sideMenuClassConfig.sideMenuClass);
  enableBodyScroll();
  sideMenu?.close();
}

/**
 * This creates a bug where clicking on the scrollbar closes the modal, but it's the lesser evil for now.
 * chrome 134 is adding a closedby attribute which will handle this out of the box, please replace with that when it's stable
 * https://developer.chrome.com/blog/chrome-134-beta#dialog_light_dismiss
 */
function handleBackDropClick(event: Event) {
  if (event.target === event.currentTarget) closeSideMenu();
}

function updateSideMenuPosition(position: string) {
  const sideMenu = select<HTMLDialogElement>(sideMenuClassConfig.sideMenuClass);
  sideMenu?.classList.remove("right", "left");
  sideMenu?.classList.add(position);
}

export function updateSideMenuBody(children: ReactElement | HTMLCollection) {
  const body = select<HTMLElement>(sideMenuClassConfig.sideMenuBodyClass);
  body && body.replaceChildren(<>{children}</>);
  focusFirstSideMenuItem();
}

export function focusFirstSideMenuItem() {
  const sideMenu = select<HTMLElement>(sideMenuClassConfig.sideMenuClass);
  if (!sideMenu) return;

  const items = Array.from(sideMenu.querySelectorAll("ul li"));

  for (const item of items) {
    const firstItem = item.querySelector("a, button");
    if (firstItem) {
      setTimeout(() => (firstItem as HTMLElement).focus(), 50);
      break;
    }
  }
}

// The default header with the Fruugo logo and close button
// Used for the MainMenu and CategoryMenus
export const DefaultSideMenuHeader = () => (
  <header class="d-flex justify-content-between align-items-center sticky-section">
    {/* We have to add a inline SVG here rather than img to stop the logo flickering between DOM updates when the browser cache is disabled */}
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="80"
      height="39"
      viewBox="0 0 66 32"
    >
      <path
        fill="#24a9fc"
        d="M41.35 16.38c0 2.3-.87 5.33-4.75 5.33s-4.75-3.03-4.75-5.33v-6.34h2.73v6.73c0 .87.24 1.83 1.02 2.3.26.16.58.25.97.26h.03c.4 0 .73-.1 1-.26.77-.47 1.01-1.43 1.01-2.3v-6.73h2.74v6.34zm-11.06 0c0 2.3-.87 5.33-4.75 5.33s-4.75-3.03-4.75-5.33v-4c-.6-.23-1.1-.42-2.02-.42-1.77 0-3.14 1.6-3.14 3.22v6.59h-2.95V10.04h2.64v2.04l.23-.5c.69-1.5 2.38-2.6 5.43-1.54h2.54v6.73c0 .88.25 1.83 1.02 2.3.26.16.58.25.97.26h.03c.4 0 .73-.1 1-.26.77-.47 1.02-1.42 1.02-2.3v-6.73h2.73v6.34zM13.86 3.62l-1.04 2.14a5.08 5.08 0 0 0-3.21-.62c-1.53.4-1.87 1.45-1.87 3.05v1.85h3.39v2.32h-3.4v9.4H4.8v-9.4h-2v-2.32h2V7.61c0-2.28 1.06-4.69 3.97-5.14a7.1 7.1 0 0 1 5.1 1.15zM66 16.28v-1.04c0-3.48-1.94-8.01-7.3-8.01-1.34 0-2.58.39-3.39.81a4.68 4.68 0 0 0-2.56-.68c-.9 0-1.82.26-2.39.6a6.22 6.22 0 0 0-3.01-.73c-2.17 0-3.56.78-4.18 1.37-.06-.23-.39-.73-1.2-.73H22.02a7.3 7.3 0 0 0-3.35-.78c-1.28 0-2.4.3-3.35.78l-.34.18c-.27.13-.62-.18-.5-.43l2.15-4.06c.27-.51.17-.89-.42-1.38A11.3 11.3 0 0 0 8.37.1C3.75.81 2.4 4.4 2.4 7.61v.26H.72c-.52 0-.72.26-.72.8v5.23c0 .56.22.74.72.74H2.4v8.33c0 .8.38 1.27 1.19 1.27h5.3c.76 0 1.13-.4 1.3-.93.2.52.52.93 1.3.93h5.26c.8 0 1.3-.47 1.3-1.27v-5.28c0-.23.1-.28.26-.28.14 0 .27.04.28.26.16 3.39 1.72 6.52 6.96 6.52 2.83 0 4.63-1.08 5.53-2.42.92 1.35 2.67 2.42 5.53 2.42 2.25 0 3.61-.66 4.5-1.5.78 2.92 4.52 3.92 6.93 3.57 1.16-.18 1.88-.8 1.94-1.42.07-.75-.54-1.07-1.54-1.01-.84.04-1.82.03-2.56-.1-1.91-.35-2.85-1.39-2.79-2.56.05-1.06.7-1.6 1.35-1.99l-.1-.08a5.16 5.16 0 0 1-1.43-3.66v-.91c0-1.99.97-4.72 4.51-4.72a4.5 4.5 0 0 1 2.9.9c.45-.5 1.3-.96 2.5-.96 1.17 0 2.13.6 2.49 1.18a4.96 4.96 0 0 1 3.24-1.12c3.93 0 5.06 3.2 5.06 5.5v.9c0 2.29-1.18 5.5-5.06 5.5-3.89 0-5.07-3.14-5.07-5.43v-.76c0-.99.22-2.02.58-2.93-.15-.4-.6-.89-1.37-.89-.67 0-1.05.38-1.2.62.3.69.44 1.6.44 2.2v.92c0 1.98-.97 4.72-4.5 4.72a6.2 6.2 0 0 1-.76-.05c-.11-.01-.17-.02-.2 0-.26.09-.42.24-.47.5-.08.47.25.75 1.23.81.58.04 1.32-.02 2.36-.02 1.76 0 3 1.25 3 3.06 0 2.2-1.74 4.34-5.31 4.34-1.78 0-3.8-.62-5.42-1.65-.21-.13-.51-.19-.72.14L40.1 28.9c-.28.44-.01.7.14.83 1.5 1.25 4.61 2.43 7.83 2.25 4.54-.25 7.77-3.42 7.2-8.54.56.34 1.94.68 3.4.68 5.82 0 7.33-4.6 7.33-7.84zm-20.6-1.53v.48c0 1.08.43 2.56 2.01 2.56 1.58 0 2.02-1.48 2.02-2.56v-.48c0-1.08-.44-2.56-2.02-2.56s-2.01 1.48-2.01 2.56zm13.14-2.56c-1.89 0-2.41 1.89-2.41 3.26v.62c0 1.37.52 3.26 2.4 3.26 1.9 0 2.42-1.89 2.42-3.26v-.62c0-1.37-.53-3.26-2.41-3.26z"
      />
    </svg>

    <button
      type="button"
      title={i18n("search.page.close")}
      onClick={closeSideMenu}
      class={`button-icon header-icon clear ${sideMenuClassConfig.closeSideMenuClass.replace(
        ".",
        ""
      )}`}
    >
      <Icon name="close" width={24} height={24} />
    </button>
  </header>
);

interface SideMenuProps {
  children?: ReactNode;
  position: SideMenuPosition;
}

const SideMenu = ({ children, position }: SideMenuProps) => {
  return (
    <dialog
      class={`${sideMenuClassConfig.sideMenuClass.replace(
        ".",
        ""
      )} ${position}`}
    >
      <div
        class={`h-100 ${sideMenuClassConfig.sideMenuBodyClass.replace(
          ".",
          ""
        )}`}
      >
        {children}
      </div>
    </dialog>
  );
};
